/**
 * Plays voice tracks along the replay with Web Audio.
 *
 * Why not `<audio>` elements: to follow the replay they must be read
 * (`currentTime`) and moved (seeks) all the time. On iPhone (WebKit, so Safari
 * and Edge alike) `currentTime` is coarse and a seek is slow, so the tracks were
 * moved again and again while playing and the sound stuttered. Here the sound
 * is scheduled on the audio clock: a source is started once at the right place
 * and runs on its own; it is restarted only when the replay jumps.
 *
 * Three ways to play a track, picked by its file:
 * - Ogg Opus (every recording of the Discord bot, most Ogg files): fetched as it
 *   comes, cut into pieces of 20 s rebuilt as small Ogg files (ogg-opus.ts),
 *   decoded one or two at a time, played back to back.
 * - Any other file up to 4 MB: decoded whole.
 * - Longer MP3, M4A, WebM, FLAC or WAV: an `<audio>` element as before, kept in
 *   step by nudging its speed rather than seeking (one seek when it is a second off).
 * A file Web Audio cannot decode falls back to the element.
 */
import { buildPiece, opusDuration, opusStream, pieceRange, readOggPages, type OggPage, type OpusStream } from "./ogg-opus.ts";

/** Seconds of sound in each decoded piece. */
const PIECE_SECONDS = 20;
/** The next piece is decoded and scheduled this long before the current one ends. */
const LOOKAHEAD_SECONDS = 6;
/** A playing source further than this from the replay is restarted (a seek, a new round). */
const RESYNC_SECONDS = 0.2;
/** Files of other kinds decoded whole up to this size; longer ones keep the element. */
const WHOLE_DECODE_BYTES = 4 * 1024 * 1024;
/** Voice is clear at 24 kHz, and decoded pieces take half the memory of 48 kHz. */
const CONTEXT_RATE = 24_000;

export type EngineKind = "pieces" | "whole" | "element";

export interface TrackStats {
  id: string;
  kind: EngineKind;
  /** Times a source was (re)started, or the element moved. */
  restarts: number;
  /** Element only: times its speed was nudged to catch up. */
  nudges: number;
  /** Pieces decoded, and the time spent on them. */
  decoded: number;
  decodeMs: number;
  /** Bytes fetched so far (pieces and whole). */
  fetched: number;
  error?: string;
}

/** What a track should be doing now, worked out on each frame of the replay. */
export interface TrackState {
  /** Where the track should be, in its own seconds (may be before 0 or past its end). */
  target: number;
  /** Playing, at a speed the voice can be heard at, and audible in the mix. */
  play: boolean;
  speed: number;
  volume: number;
}

interface Player {
  readonly stats: TrackStats;
  sync(state: TrackState): void;
  /** The audio clock stopped (page hidden) or came back: start again from where the replay is. */
  reset(): void;
  dispose(): void;
}

// ————— The audio context —————

let context: AudioContext | null = null;
const contextListeners = new Set<() => void>();

type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };

function makeContext(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  // iPhone: Web Audio is silenced by the ring/silent switch unless the page says it plays media.
  const session = (navigator as AudioSessionNavigator).audioSession;
  if (session) session.type = "playback";
  try {
    return new Ctor({ sampleRate: CONTEXT_RATE, latencyHint: "playback" });
  } catch {
    return new Ctor();
  }
}

/**
 * Browsers start sound only after a tap or a key (iPhone: inside the event
 * itself). The first one, anywhere on the page, makes and wakes the context;
 * the Play button is such a tap, and capture runs before its handler.
 */
function unlock() {
  if (!context) {
    context = makeContext();
    if (!context) return;
    for (const listener of contextListeners) listener();
  }
  if (context.state !== "running") void context.resume().catch(() => undefined);
}

let unlockInstalled = false;
function installUnlock() {
  if (unlockInstalled || typeof document === "undefined") return;
  unlockInstalled = true;
  for (const type of ["pointerdown", "keydown", "touchend"]) document.addEventListener(type, unlock, { capture: true, passive: true });
}

/** Web Audio is there, and `?voice=element` does not ask for the elements (to compare on a phone). */
export function webAudioAvailable(): boolean {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).get("voice") === "element") return false;
  return Boolean(window.AudioContext ?? (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext);
}

const decode = (ctx: AudioContext, data: ArrayBuffer) =>
  new Promise<AudioBuffer>((resolve, reject) => {
    // Old WebKit only takes callbacks; newer ones return a promise too.
    const p = ctx.decodeAudioData(data, resolve, reject) as Promise<AudioBuffer> | undefined;
    p?.then(resolve, reject);
  });

// ————— Scheduled playback, shared by pieces and whole files —————

interface Piece {
  /** The time, in the track, of the buffer's first sample. */
  start: number;
  buffer: AudioBuffer;
}

/**
 * Plays a track that comes as decoded pieces: starts the piece under the
 * target, schedules the next one to start exactly where it ends, and keeps a
 * line from the audio clock to the track's time to see when the replay jumped.
 */
abstract class ScheduledPlayer implements Player {
  readonly stats: TrackStats;
  protected readonly gain: GainNode;
  private sources: AudioBufferSourceNode[] = [];
  /** Track time `time` was at audio time `at`, going at `rate`. */
  private anchor: { time: number; at: number; rate: number } | null = null;
  /** Where the scheduled sound ends, in the track; the next piece starts there. */
  private scheduledEnd = 0;
  private generation = 0;
  /** A restart is decoding the piece it needs. */
  private pending = false;
  /** The next piece is being decoded ahead. */
  private ahead = false;
  private last: TrackState | null = null;
  private timer: number;
  protected disposed = false;

  protected readonly ctx: AudioContext;
  private readonly onFail: (error: string) => void;

  constructor(ctx: AudioContext, id: string, kind: EngineKind, onFail: (error: string) => void) {
    this.ctx = ctx;
    this.onFail = onFail;
    this.stats = { id, kind, restarts: 0, nudges: 0, decoded: 0, decodeMs: 0, fetched: 0 };
    this.gain = ctx.createGain();
    this.gain.connect(ctx.destination);
    this.timer = window.setInterval(() => this.lookAhead(), 500);
  }

  /** The piece that holds `time`, decoded; null when the track has nothing there (yet). */
  protected abstract piece(time: number): Promise<Piece | null>;
  /** The track's length, once known. */
  protected abstract length(): number | undefined;

  protected fail(error: string) {
    this.stats.error = error;
    this.stop();
    this.onFail(error);
  }

  private now(): number | null {
    if (!this.anchor) return null;
    return this.anchor.time + (this.ctx.currentTime - this.anchor.at) * this.anchor.rate;
  }

  sync(state: TrackState) {
    this.last = state;
    if (this.disposed) return;
    this.gain.gain.value = Math.max(0, Math.min(1, state.volume));
    const end = this.length();
    const wanted = state.play && this.ctx.state === "running" && !(end !== undefined && state.target >= end);
    if (!wanted) {
      if (this.anchor || this.pending) this.stop();
      return;
    }
    if (this.pending) return;
    const now = this.now();
    if (now === null || this.anchor?.rate !== state.speed || Math.abs(now - state.target) > RESYNC_SECONDS) this.restart();
  }

  reset() {
    this.stop();
    if (this.last) this.sync(this.last);
  }

  private stop() {
    this.generation++;
    this.pending = false;
    this.ahead = false;
    this.anchor = null;
    for (const s of this.sources) {
      s.onended = null;
      try {
        s.stop();
      } catch {
        // Not started yet, or already over.
      }
      s.disconnect();
    }
    this.sources = [];
  }

  private restart() {
    this.stop();
    const state = this.last;
    if (!state) return;
    // Before the recording starts: wait until it is a few seconds away, then schedule its start.
    if (state.target < 0 && -state.target / state.speed > LOOKAHEAD_SECONDS / 2) return;
    const generation = this.generation;
    this.pending = true;
    this.stats.restarts++;
    void this.piece(Math.max(0, state.target)).then(
      (piece) => {
        if (generation !== this.generation || this.disposed) return;
        this.pending = false;
        const latest = this.last;
        if (!piece || !latest?.play || this.ctx.state !== "running") return;
        // Decoding took a moment: start from where the replay is now.
        const target = latest.target;
        const delay = target < 0 ? -target / latest.speed : 0;
        const at = this.ctx.currentTime + delay;
        const offset = Math.max(0, target) - piece.start;
        if (offset >= piece.buffer.duration) return;
        this.anchor = { time: target, at: this.ctx.currentTime, rate: latest.speed };
        this.play(piece, at, Math.max(0, offset), latest.speed);
      },
      () => {
        if (generation === this.generation) this.pending = false;
      },
    );
  }

  private play(piece: Piece, at: number, offset: number, rate: number) {
    const source = this.ctx.createBufferSource();
    source.buffer = piece.buffer;
    source.playbackRate.value = rate;
    source.connect(this.gain);
    source.onended = () => {
      this.sources = this.sources.filter((s) => s !== source);
      source.disconnect();
    };
    source.start(at, offset);
    this.sources.push(source);
    this.scheduledEnd = piece.start + piece.buffer.duration;
  }

  /** Decodes and schedules the next piece before the current one runs out. */
  private lookAhead() {
    if (this.disposed || this.pending || this.ahead || !this.anchor || !this.last?.play) return;
    const now = this.now();
    if (now === null) return;
    const end = this.length();
    if (end !== undefined && this.scheduledEnd >= end - 0.01) return;
    if (this.scheduledEnd - now > LOOKAHEAD_SECONDS * this.anchor.rate) return;
    const generation = this.generation;
    const from = this.scheduledEnd;
    this.ahead = true;
    void this.piece(from).then(
      (piece) => {
        if (generation !== this.generation || this.disposed) return;
        this.ahead = false;
        if (!piece || !this.anchor) return;
        const offset = from - piece.start;
        if (offset < 0 || offset >= piece.buffer.duration) return;
        const at = this.anchor.at + (from - this.anchor.time) / this.anchor.rate;
        // Too late to join without a gap: sync() restarts on the next frame.
        if (at < this.ctx.currentTime) return;
        this.play(piece, at, offset, this.anchor.rate);
      },
      () => {
        if (generation === this.generation) this.ahead = false;
      },
    );
  }

  dispose() {
    this.disposed = true;
    this.stop();
    window.clearInterval(this.timer);
    this.gain.disconnect();
  }
}

/** A short file of any kind, decoded once. */
class WholePlayer extends ScheduledPlayer {
  private decoded: Promise<Piece | null>;
  private duration: number | undefined;

  constructor(ctx: AudioContext, id: string, url: string, onDuration: (s: number) => void, onFail: (error: string) => void) {
    super(ctx, id, "whole", onFail);
    this.decoded = (async () => {
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.arrayBuffer();
      this.stats.fetched = data.byteLength;
      const t0 = performance.now();
      const buffer = await decode(ctx, data);
      this.stats.decoded = 1;
      this.stats.decodeMs = Math.round(performance.now() - t0);
      this.duration = buffer.duration;
      onDuration(buffer.duration);
      return { start: 0, buffer };
    })().catch((e: unknown) => {
      if (!this.disposed) this.fail(e instanceof Error ? e.message : String(e));
      return null;
    });
  }

  protected piece() {
    return this.decoded;
  }

  protected length() {
    return this.duration;
  }
}

/** An Ogg Opus file, fetched as it comes and decoded 20 s at a time. */
class PiecesPlayer extends ScheduledPlayer {
  private bytes = new Uint8Array(0);
  private filled = 0;
  private pages: OggPage[] = [];
  private stream: OpusStream | null = null;
  private complete = false;
  private duration: number | undefined;
  private waiting: (() => void)[] = [];
  /** Decoded pieces by index (20 s each), the last few kept. */
  private cache = new Map<number, Promise<Piece | null>>();
  private abort = new AbortController();

  private readonly onDuration: (s: number) => void;
  private readonly notOpus: () => void;

  constructor(ctx: AudioContext, id: string, url: string, size: number, onDuration: (s: number) => void, onFail: (error: string) => void, notOpus: () => void) {
    super(ctx, id, "pieces", onFail);
    this.onDuration = onDuration;
    this.notOpus = notOpus;
    this.bytes = new Uint8Array(Math.max(64 * 1024, size));
    void this.download(url).catch((e: unknown) => {
      if (!this.disposed && !this.abort.signal.aborted) this.fail(e instanceof Error ? e.message : String(e));
    });
  }

  private async download(url: string) {
    const response = await fetch(url, { credentials: "same-origin", signal: this.abort.signal });
    if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
    const reader = response.body.getReader();
    let next = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (this.filled + value.length > this.bytes.length) {
        const grown = new Uint8Array(Math.max(this.bytes.length * 2, this.filled + value.length));
        grown.set(this.bytes.subarray(0, this.filled));
        this.bytes = grown;
      }
      this.bytes.set(value, this.filled);
      this.filled += value.length;
      this.stats.fetched = this.filled;
      const read = readOggPages(this.bytes.subarray(0, this.filled), next);
      next = read.next;
      if (read.pages.length) {
        this.pages.push(...read.pages);
        this.stream = opusStream(this.bytes, this.pages);
        if (!this.stream && this.pages.length >= 1) {
          // Vorbis, FLAC in Ogg, a video: not cut here.
          this.abort.abort();
          this.notOpus();
          return;
        }
        this.wake();
      }
    }
    this.complete = true;
    if (this.stream) {
      this.duration = opusDuration(this.stream);
      this.onDuration(this.duration);
    }
    this.wake();
  }

  private wake() {
    const waiting = this.waiting;
    this.waiting = [];
    for (const w of waiting) w();
  }

  protected length() {
    return this.duration;
  }

  protected piece(time: number): Promise<Piece | null> {
    return this.holding(Math.floor(time / PIECE_SECONDS), time);
  }

  /**
   * The piece that has sound at `time`. Pieces are cut on pages, so piece n may
   * start a little before n × 20 s and end a hair before (n + 1) × 20 s: when it
   * runs out right at `time`, the next one is the one wanted.
   */
  private async holding(index: number, time: number): Promise<Piece | null> {
    const piece = await (this.cache.get(index) ?? this.make(index, time));
    if (!piece || piece.start > time + 1e-3) return piece && index > 0 ? this.holding(index - 1, time) : piece;
    if (time < piece.start + piece.buffer.duration - 0.02) return piece;
    const end = this.length();
    if (end !== undefined && time >= end - 0.02) return null;
    return this.cache.get(index + 1) ?? this.make(index + 1, time);
  }

  private make(index: number, time: number): Promise<Piece | null> {
    const made = this.build(index, time);
    this.cache.set(index, made);
    // Keep the piece before, this one and the next: a seek back or forward of a few seconds needs no new decode.
    for (const key of this.cache.keys()) if (key < index - 1 || key > index + 1) this.cache.delete(key);
    return made;
  }

  private async build(index: number, time: number): Promise<Piece | null> {
    const from = Math.min(time, index * PIECE_SECONDS);
    const to = (index + 1) * PIECE_SECONDS;
    for (;;) {
      if (this.disposed) return null;
      const stream = this.stream;
      const range = stream ? pieceRange(stream, from, to) : null;
      // Wait for the pages up to the end of the piece, unless the file has no more.
      if (stream && range && (!range.atEnd || this.complete || (stream.audioPages[range.last].granule - stream.preSkip) / 48_000 >= to)) {
        const data = buildPiece(this.bytes, stream, range);
        const t0 = performance.now();
        try {
          const buffer = await decode(this.ctx, data.buffer as ArrayBuffer);
          this.stats.decoded++;
          this.stats.decodeMs += Math.round(performance.now() - t0);
          return { start: range.start, buffer };
        } catch (e) {
          // Web Audio here does not read Ogg Opus: play the file with an element.
          const why = e instanceof Error ? e.message : "Cannot decode Ogg Opus";
          if (this.stats.decoded === 0) this.fail(why);
          else this.stats.error = `piece ${index}: ${why}`;
          return null;
        }
      }
      if (this.complete) return null;
      await new Promise<void>((resolve) => this.waiting.push(resolve));
    }
  }

  dispose() {
    this.abort.abort();
    super.dispose();
    this.wake();
  }
}

/** The `<audio>` element, for files Web Audio is not used for. */
class ElementPlayer implements Player {
  readonly stats: TrackStats;
  private el = new Audio();
  private lastSeek = 0;

  constructor(id: string, url: string, onDuration: (s: number) => void, error?: string) {
    this.stats = { id, kind: "element", restarts: 0, nudges: 0, decoded: 0, decodeMs: 0, fetched: 0, error };
    this.el.preload = "metadata";
    this.el.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(this.el.duration)) onDuration(this.el.duration);
    });
    this.el.src = url;
  }

  sync({ target, play, speed, volume }: TrackState) {
    const el = this.el;
    el.volume = Math.max(0, Math.min(1, volume));
    const inside = target >= 0 && (!Number.isFinite(el.duration) || target < el.duration - 0.05);
    if (!play || !inside) {
      if (!el.paused) el.pause();
      return;
    }
    if (el.paused) {
      this.seek(target);
      el.playbackRate = speed;
      void el.play().catch(() => undefined);
      return;
    }
    const drift = el.currentTime - target;
    // A jump in the replay, or far behind: move it, but not more than once a second (seeks are slow on iPhone).
    if (Math.abs(drift) > 1 && !el.seeking && performance.now() - this.lastSeek > 1000) {
      this.seek(target);
      el.playbackRate = speed;
      return;
    }
    // A little off: play 5 % faster or slower until it is back, which is not heard; a seek would be.
    const rate = Math.abs(drift) > 0.12 ? speed * (drift > 0 ? 0.95 : 1.05) : speed;
    if (Math.abs(el.playbackRate - rate) > 1e-3) {
      if (rate !== speed) this.stats.nudges++;
      el.playbackRate = rate;
    }
  }

  private seek(target: number) {
    this.el.currentTime = target;
    this.lastSeek = performance.now();
    this.stats.restarts++;
  }

  reset() {
    this.el.pause();
  }

  dispose() {
    this.el.pause();
    this.el.removeAttribute("src");
    this.el.load();
  }
}

// ————— The engine: one per open map —————

export interface EngineTrack {
  id: string;
  url: string;
  contentType: string;
  bytes: number;
}

export class VoiceEngine {
  private players = new Map<string, Player>();
  private tracks = new Map<string, EngineTrack>();
  private states = new Map<string, TrackState>();
  private readonly useWebAudio = webAudioAvailable();

  private readonly onDuration: (id: string, seconds: number) => void;

  constructor(onDuration: (id: string, seconds: number) => void) {
    this.onDuration = onDuration;
    installUnlock();
    contextListeners.add(this.onContext);
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  /** The context was just made by a tap: tracks waiting on it can now use Web Audio. */
  private onContext = () => {
    for (const [id, player] of this.players) {
      if (player.stats.kind !== "element" || player.stats.error) continue;
      const track = this.tracks.get(id);
      if (!track || !this.wantsWebAudio(track)) continue;
      player.dispose();
      this.players.set(id, this.make(track));
      const state = this.states.get(id);
      if (state) this.players.get(id)?.sync(state);
    }
  };

  private onVisibility = () => {
    if (!context) return;
    if (document.hidden) void context.suspend().catch(() => undefined);
    else {
      void context.resume().catch(() => undefined);
      for (const p of this.players.values()) p.reset();
    }
  };

  private wantsWebAudio(track: EngineTrack) {
    return this.useWebAudio && (track.contentType === "audio/ogg" || track.bytes <= WHOLE_DECODE_BYTES);
  }

  private make(track: EngineTrack, error?: string): Player {
    const duration = (s: number) => this.onDuration(track.id, s);
    // Until the first tap there is no context: the element shows the length meanwhile.
    if (error || !context || !this.wantsWebAudio(track)) return new ElementPlayer(track.id, track.url, duration, error);
    const ctx = context;
    const fallback = (why: string) => {
      if (this.tracks.get(track.id) !== track) return;
      this.players.get(track.id)?.dispose();
      const player = this.make(track, why);
      this.players.set(track.id, player);
      const state = this.states.get(track.id);
      if (state) player.sync(state);
    };
    if (track.contentType === "audio/ogg") {
      return new PiecesPlayer(ctx, track.id, track.url, track.bytes, duration, fallback, () => {
        if (track.bytes <= WHOLE_DECODE_BYTES) {
          // Ogg but not Opus (Vorbis): decoded whole when short.
          if (this.tracks.get(track.id) !== track) return;
          this.players.get(track.id)?.dispose();
          const player = new WholePlayer(ctx, track.id, track.url, duration, fallback);
          this.players.set(track.id, player);
        } else fallback("Ogg file without Opus");
      });
    }
    return new WholePlayer(ctx, track.id, track.url, duration, fallback);
  }

  setTracks(list: EngineTrack[]) {
    for (const track of list) {
      const known = this.tracks.get(track.id);
      if (known && known.url === track.url) continue;
      this.players.get(track.id)?.dispose();
      this.tracks.set(track.id, track);
      this.players.set(track.id, this.make(track));
    }
    for (const id of [...this.tracks.keys()]) {
      if (list.some((t) => t.id === id)) continue;
      this.players.get(id)?.dispose();
      this.players.delete(id);
      this.tracks.delete(id);
      this.states.delete(id);
    }
  }

  sync(id: string, state: TrackState) {
    this.states.set(id, state);
    this.players.get(id)?.sync(state);
  }

  stats(): TrackStats[] {
    return [...this.players.values()].map((p) => ({ ...p.stats }));
  }

  dispose() {
    contextListeners.delete(this.onContext);
    document.removeEventListener("visibilitychange", this.onVisibility);
    for (const p of this.players.values()) p.dispose();
    this.players.clear();
    this.tracks.clear();
  }
}
